import styled from 'styled-components';
import { Radio } from 'antd';
import { ITheme } from '@app/styles/themes/types';

export const SelectedBadge = styled.span`
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
  z-index: 1;
  display: none;
  align-items: center;
  justify-content: center;
  width: 1.125rem;
  height: 1.125rem;
  color: var(--text-secondary-color);
  font-size: 0.625rem;
  background-color: var(--primary-color);
  border: 2px solid var(--background-color);
  border-radius: 50%;
`;

export const ThemeOptions = styled(Radio.Group)`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.5rem;
  width: 100%;
`;

export const ThemeOption = styled(Radio)`
  position: relative;
  display: flex;
  min-width: 0;
  margin: 0;
  padding: 0.4375rem;
  overflow: hidden;
  color: var(--text-main-color);
  background-color: var(--background-color);
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  cursor: pointer;
  transition: border-color 180ms ease, box-shadow 180ms ease,
    transform 180ms ease, background-color 180ms ease;

  & > span:last-child {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;
    padding: 0;
  }

  .ant-radio {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    opacity: 0;
  }

  &:hover {
    border-color: var(--primary-text-color);
    box-shadow: var(--box-shadow-hover);
    transform: translateY(-1px);
  }

  &:focus-within {
    border-color: var(--primary-text-color);
    box-shadow: 0 0 0 2px rgba(var(--primary-text-rgb-color), 0.3);
  }

  &.ant-radio-wrapper-checked {
    padding: 0.375rem;
    background-color: var(--secondary-background-color);
    border: 2px solid var(--primary-text-color);

    ${SelectedBadge} {
      display: flex;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;
    }
  }
`;

export const ThemePreview = styled.div<{ $theme: ITheme }>`
  --preview-primary: ${({ $theme }) => $theme.primary};
  --preview-header: ${({ $theme }) => $theme.primaryGradient};
  --preview-page: ${({ $theme }) => $theme.layoutBodyBg};
  --preview-surface: ${({ $theme }) => $theme.background};
  --preview-sidebar: ${({ $theme }) => $theme.layoutSiderBg};
  --preview-table-header: ${({ $theme }) => $theme.tableHeaderBackground};
  --preview-line: ${({ $theme }) => $theme.textLight};
  --preview-border: ${({ $theme }) => $theme.tableBorderColor};

  width: 100%;
  height: 4.25rem;
  overflow: hidden;
  background: var(--preview-page);
  border: 1px solid var(--preview-border);
  border-radius: 0.375rem;
`;

export const PreviewHeader = styled.div`
  height: 0.75rem;
  background: var(--preview-header);
`;

export const PreviewLayout = styled.div`
  display: flex;
  height: calc(100% - 0.75rem);
`;

export const PreviewSidebar = styled.div`
  width: 22%;
  background: var(--preview-sidebar);
`;

export const PreviewContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.25rem;
  margin: 0.375rem;
  padding: 0.25rem;
  background: var(--preview-surface);
  border: 1px solid var(--preview-border);
  border-radius: 0.1875rem;
`;

export const PreviewTableHeader = styled.div`
  height: 0.375rem;
  background: var(--preview-table-header);
  border-bottom: 1px solid var(--preview-primary);
  border-radius: 0.125rem 0.125rem 0 0;
`;

export const PreviewLine = styled.span<{ $short?: boolean }>`
  display: block;
  width: ${({ $short }) => ($short ? '55%' : '82%')};
  height: 0.1875rem;
  background: var(--preview-line);
  border-radius: 999px;
  opacity: 0.72;
`;

export const PreviewButton = styled.span`
  display: block;
  width: 36%;
  height: 0.375rem;
  margin-top: auto;
  margin-left: auto;
  background: var(--preview-primary);
  border-radius: 999px;
`;

export const ThemeLabel = styled.span`
  display: block;
  margin-top: 0.375rem;
  overflow: hidden;
  color: var(--text-main-color);
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1rem;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
